import React, { useState, useEffect } from 'react';
import { CrudList, CrudStyles } from '../shared/Crud';
import VehicleForm from './VehicleForm';
import vehicleService from '../../services/vehicleService';
import { EditIcon, CheckIcon, XIcon } from '../Layout/icons';
import { Button } from '../shared/Crud/styles';
import MessageModal from '../shared/MessageModal';

const { FilterGroup, StatusBadge } = CrudStyles;

export default function Vehicles() {
    const [view, setView] = useState('list'); // 'list', 'create', 'edit'
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });
    const [sorting, setSorting] = useState({
        field: 'model',
        direction: 'asc',
    });
    const [filters, setFilters] = useState({
        model: '',
        licensePlate: '',
        chassis: '',
        active: 'true', // Padrão: Ativo
    });
    const [localFilters, setLocalFilters] = useState({
        model: '',
        licensePlate: '',
        chassis: '',
        active: 'true', // Padrão: Ativo
    });
    const [editingItem, setEditingItem] = useState(null);
    const isInitialMount = React.useRef(true);
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    // Debounce logic: Update filters (and reset page) when localFilters changes after delay
    useEffect(() => {
        // Skip debounce on initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const timer = setTimeout(() => {
            setFilters(localFilters);
            setPagination(prev => ({ ...prev, page: 0 }));
        }, 750);
        return () => clearTimeout(timer);
    }, [localFilters]);

    // Fetch data when dependencies change
    useEffect(() => {
        const abortController = new AbortController();

        const fetchData = async () => {
            setLoading(true);
            try {
                const result = await vehicleService.list({
                    page: pagination.page,
                    size: 5,
                    sort: `${sorting.field},${sorting.direction}`,
                    ...filters,
                });

                // Only update state if request wasn't aborted
                if (!abortController.signal.aborted) {
                    setData(result.content || []);
                    const totalPages = result.page?.totalPages ?? 0;
                    const pageNumber = totalPages === 0 ? 0 : Math.min(result.page?.number ?? 0, Math.max(0, totalPages - 1));
                    setPagination(prev => ({
                        ...prev,
                        page: pageNumber,
                        totalPages,
                        first: totalPages === 0 || pageNumber === 0,
                        last: totalPages === 0 || pageNumber >= totalPages - 1,
                    }));
                }
            } catch (error) {
                // Error fetching vehicles
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        // Cleanup function to abort request if component unmounts or dependencies change
        return () => {
            abortController.abort();
        };
    }, [pagination.page, sorting.field, sorting.direction, filters, refetchTrigger]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleSort = (field, direction) => {
        setSorting({ field, direction });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = () => {
        setFilters({ ...localFilters });
        setPagination(prev => ({ ...prev, page: 0 }));
        setRefetchTrigger(prev => prev + 1);
    };

    const handleCreate = async (formData) => {
        try {
            await vehicleService.create(formData);
            setView('list');
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            throw error;
        }
    };

    const handleUpdate = async (formData) => {
        try {
            await vehicleService.update(editingItem.id, formData);
            setView('list');
            setEditingItem(null);
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            throw error;
        }
    };

    const handleToggleStatus = async (id) => {
        if (!window.confirm('Tem certeza que deseja alterar o status deste veículo?')) return;

        try {
            await vehicleService.toggleStatus(id);
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: 'Erro ao alterar status',
            });
        }
    };

    const columns = [
        { header: 'ID', accessor: 'id', sortable: true },
        { header: 'Modelo', accessor: 'model', sortable: true },
        { header: 'Marca', accessor: 'brand', sortable: true },
        { header: 'Placa', accessor: 'licensePlate', sortable: false },
        { header: 'Ano', accessor: 'year', sortable: true },
        { header: 'Cor', accessor: 'color', sortable: false },
        {
            header: 'Status',
            accessor: 'active',
            sortable: false,
            render: (item) => (
                <StatusBadge $active={item.active}>
                    {item.active ? 'Ativo' : 'Inativo'}
                </StatusBadge>
            )
        },
    ];

    const renderActions = (item) => (
        <>
            <Button
                variant="outline"
                onClick={() => {
                    setEditingItem(item);
                    setView('edit');
                }}
                title="Editar"
            >
                <EditIcon />
            </Button>
            <Button
                variant={item.active ? 'danger' : 'primary'}
                onClick={() => handleToggleStatus(item.id)}
                title={item.active ? 'Inativar' : 'Ativar'}
            >
                {item.active ? <XIcon /> : <CheckIcon />}
            </Button>
        </>
    );

    const renderFilters = (
        <>
            <FilterGroup>
                <label>Modelo</label>
                <div style={{ position: 'relative' }}>
                    <input
                        name="model"
                        value={localFilters.model}
                        onChange={handleFilterChange}
                        placeholder="Buscar por modelo..."
                    />
                </div>
            </FilterGroup>
            <FilterGroup>
                <label>Placa</label>
                <input
                    name="licensePlate"
                    value={localFilters.licensePlate}
                    onChange={handleFilterChange}
                    placeholder="Buscar por placa..."
                />
            </FilterGroup>
            <FilterGroup>
                <label>Chassis</label>
                <input
                    name="chassis"
                    value={localFilters.chassis}
                    onChange={handleFilterChange}
                    placeholder="Buscar por chassis..."
                />
            </FilterGroup>
            <FilterGroup>
                <label>Status</label>
                <select name="active" value={localFilters.active} onChange={handleFilterChange}>
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                </select>
            </FilterGroup>
        </>
    );

    const messageModalEl = messageModal.open ? (
        <MessageModal
            title={messageModal.title}
            message={messageModal.message}
            onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
        />
    ) : null;

    if (view === 'create') {
        return (
            <>
                {messageModalEl}
                <VehicleForm
                    onSubmit={handleCreate}
                    onCancel={() => setView('list')}
                />
            </>
        );
    }

    if (view === 'edit') {
        return (
            <>
                {messageModalEl}
                <VehicleForm
                    initialData={editingItem}
                    onSubmit={handleUpdate}
                    onCancel={() => {
                        setView('list');
                        setEditingItem(null);
                    }}
                />
            </>
        );
    }

    return (
        <>
            {messageModalEl}
            <CrudList
                title="Veículos"
                onNew={() => setView('create')}
                onSearch={handleSearch}
                filters={renderFilters}
                columns={columns}
                data={data}
                loading={loading}
                pagination={{
                    ...pagination,
                    onPageChange: handlePageChange,
                }}
                sorting={{
                    ...sorting,
                    onSort: handleSort,
                }}
                actions={renderActions}
            />
        </>
    );
}
